/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { AssistantEvent, AssistantService, Citation, ConversationSummary } from './assistant.service';

interface Step {
  id: string;
  kind: string;
  title: string;
  sig?: string | null;
  summary?: string;
  lines?: string[] | null;
  ms?: number;
  state: 'run' | 'done' | 'fail' | 'stopped';
}

interface Turn {
  question: string;
  aboutDocument: boolean;
  steps: Step[];
  answer: string;
  citations: Citation[];
  meta?: string;
  error?: string;
  state: 'run' | 'done' | 'stopped' | 'error';
}

/** A piece of an answer: plain text, or a citation chip. */
interface Piece {
  text?: string;
  cite?: Citation;
  pending?: boolean;
}

/**
 * The chat button in the corner, and the window it opens.
 *
 * Shown on every signed-in page, but only while the assistant is switched on
 * in Settings — turning it off there takes it away from everyone. On a
 * document's page the window offers to answer about that document alone.
 *
 * Each step the assistant takes arrives as an event and is drawn as it
 * happens; nothing here guesses what the assistant is doing.
 */
@Component({
  selector: 'app-assistant-launcher',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './assistant-launcher.component.html',
  styleUrls: ['./assistant-launcher.component.scss']
})
export class AssistantLauncherComponent implements OnInit {
  private readonly assistant = inject(AssistantService);
  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('body') private readonly body?: ElementRef<HTMLElement>;

  readonly status = this.assistant.status;
  private readonly signedIn = signal(false);
  private readonly onLoginPage = signal(true);
  readonly visible = computed(() => this.signedIn() && !this.onLoginPage() && !!this.status()?.enabled);

  readonly open = signal(false);
  readonly view = signal<'chat' | 'history'>('chat');
  readonly turns = signal<Turn[]>([]);
  readonly busy = signal(false);
  readonly conversationId = signal<string | null>(null);
  readonly history = signal<ConversationSummary[] | null>(null);

  /** The document on screen, when there is one. */
  readonly documentId = signal<string | null>(null);
  /** Whether to ask about that document alone, or the whole repository. */
  readonly aboutDocument = signal(true);

  question = '';
  private abort?: AbortController;

  readonly suggestions = ['DMS.ASSISTANT.SUGGEST_1', 'DMS.ASSISTANT.SUGGEST_2', 'DMS.ASSISTANT.SUGGEST_3'];
  readonly documentSuggestions = ['DMS.ASSISTANT.SUGGEST_DOC_1', 'DMS.ASSISTANT.SUGGEST_DOC_2'];

  ngOnInit(): void {
    this.signedIn.set(this.auth.isLoggedIn());
    this.follow(this.router.url);
    if (this.signedIn()) {
      this.assistant.refreshStatus();
    }

    this.auth.onLogin.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.signedIn.set(true);
      this.assistant.refreshStatus();
    });
    this.auth.onLogout.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.signedIn.set(false);
      this.close();
      this.newChat();
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.follow((event as NavigationEnd).urlAfterRedirects));
  }

  private follow(url: string): void {
    this.onLoginPage.set(/^\/?(login|logout)\b/.test(url.replace(/^\/#/, '')));
    const match = /\/document\/([^/?#;]+)/.exec(url);
    const id = match ? decodeURIComponent(match[1]) : null;
    if (id !== this.documentId()) {
      this.documentId.set(id);
      this.aboutDocument.set(true);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) {
      this.close();
    }
  }

  toggle(): void {
    if (this.open()) {
      this.close();
      return;
    }
    this.open.set(true);
    this.assistant.refreshStatus();
  }

  close(): void {
    this.open.set(false);
  }

  newChat(): void {
    this.abort?.abort();
    this.turns.set([]);
    this.conversationId.set(null);
    this.view.set('chat');
  }

  // ── asking ──────────────────────────────────────────────────────────────

  onEnter(event: Event): void {
    const key = event as KeyboardEvent;
    if (!key.shiftKey && !key.isComposing) {
      key.preventDefault();
      void this.ask();
    }
  }

  async ask(text?: string): Promise<void> {
    const question = (text ?? this.question).trim();
    if (!question || this.busy()) {
      return;
    }

    this.question = '';
    this.view.set('chat');
    this.busy.set(true);
    const aboutDocument = !!this.documentId() && this.aboutDocument();
    const turn: Turn = { question, aboutDocument, steps: [], answer: '', citations: [], state: 'run' };
    this.turns.update((turns) => [...turns, turn]);
    this.scrollDown();

    this.abort = new AbortController();
    try {
      await this.assistant.chat(
        { question, conversationId: this.conversationId(), documentId: aboutDocument ? this.documentId() : null },
        (event) => this.zone.run(() => this.apply(turn, event)),
        this.abort.signal
      );
      if (turn.state === 'run') {
        turn.state = 'done';
      }
    } catch (error) {
      if (this.abort.signal.aborted) {
        turn.state = 'stopped';
        turn.steps.filter((s) => s.state === 'run').forEach((s) => (s.state = 'stopped'));
      } else {
        turn.state = 'error';
        turn.error = this.translate.instant('DMS.ASSISTANT.UNREACHABLE');
      }
    } finally {
      this.busy.set(false);
      this.turns.update((turns) => [...turns]);
      this.scrollDown();
    }
  }

  /** A suggestion is a translation key; what is asked is its text in the current language. */
  askKey(key: string): void {
    void this.ask(this.translate.instant(key));
  }

  stop(): void {
    this.abort?.abort();
  }

  private apply(turn: Turn, event: AssistantEvent): void {
    switch (event.type) {
      case 'step.start':
        turn.steps.push({ id: event.id, kind: event.kind, title: event.title, sig: event.sig, state: 'run' });
        break;
      case 'step.end': {
        const step = turn.steps.find((s) => s.id === event.id);
        if (step) {
          step.summary = event.summary;
          step.lines = event.lines;
          step.ms = event.ms;
          step.state = event.ok ? 'done' : 'fail';
        }
        break;
      }
      case 'answer.delta':
        turn.answer += event.text;
        break;
      case 'answer.reset':
        turn.answer = '';
        break;
      case 'citation':
        turn.citations.push(event);
        break;
      case 'done':
        turn.meta = this.translate.instant('DMS.ASSISTANT.META', {
          seconds: (event.ms / 1000).toFixed(1),
          pages: event.pagesRead,
          documents: event.documents,
          model: event.model
        });
        break;
      case 'saved':
        this.conversationId.set(event.conversationId);
        break;
      case 'error':
        turn.error = event.message;
        turn.state = 'error';
        turn.steps.filter((s) => s.state === 'run').forEach((s) => (s.state = 'fail'));
        break;
      default:
        break;
    }
    this.turns.update((turns) => [...turns]);
    this.scrollDown();
  }

  /**
   * The answer cut into text and citation chips. A marker the server has not
   * confirmed yet shows as a placeholder while the answer is still coming; one
   * it never confirms — a reference to nothing this question read — is dropped.
   */
  pieces(turn: Turn): Piece[] {
    const pieces: Piece[] = [];
    const marker = /\[D\d+[^\]]*\]/gi;
    let last = 0;
    for (const match of turn.answer.matchAll(marker)) {
      pieces.push({ text: turn.answer.slice(last, match.index) });
      const cite = turn.citations.find((c) => c.marker === match[0]);
      if (cite) {
        pieces.push({ cite });
      } else if (turn.state === 'run') {
        pieces.push({ pending: true });
      }
      last = (match.index ?? 0) + match[0].length;
    }
    pieces.push({ text: turn.answer.slice(last) });
    return pieces;
  }

  /** The documents an answer cited, once each, with the pages it cited them for. */
  sources(turn: Turn): { n: number; nodeId: string; name: string; pages: number[] }[] {
    const byNode = new Map<string, { n: number; nodeId: string; name: string; pages: number[] }>();
    for (const c of turn.citations) {
      const entry = byNode.get(c.nodeId) ?? { n: c.n, nodeId: c.nodeId, name: c.name, pages: [] };
      if (c.page && !entry.pages.includes(c.page)) {
        entry.pages.push(c.page);
      }
      byNode.set(c.nodeId, entry);
    }
    return [...byNode.values()];
  }

  /**
   * pieces() and sources() build new objects every time the view is checked.
   * Without these, Angular replaced the buttons on each check — and pressing the
   * mouse is a check, so the click landed on a button already gone and a source
   * never opened. Found in testing.
   */
  trackByIndex(index: number): number {
    return index;
  }

  trackBySource(_index: number, source: { nodeId: string }): string {
    return source.nodeId;
  }

  openDocument(nodeId: string): void {
    void this.router.navigate(['/document', nodeId]);
  }

  // ── history ─────────────────────────────────────────────────────────────

  showHistory(): void {
    if (this.view() === 'history') {
      this.view.set('chat');
      return;
    }
    this.view.set('history');
    this.history.set(null);
    this.assistant.history().subscribe({
      next: ({ conversations }) => this.history.set(conversations),
      error: () => this.history.set([])
    });
  }

  loadConversation(id: string): void {
    this.assistant.conversation(id).subscribe((conversation) => {
      const turns: Turn[] = (conversation?.turns ?? []).map((t: any) => ({
        question: t.question,
        aboutDocument: false,
        steps: (t.steps ?? []).map((s: any, i: number) => ({
          id: 'h' + i,
          kind: s.kind,
          title: s.title,
          summary: s.summary,
          ms: s.ms,
          state: 'done'
        })),
        answer: t.answer ?? '',
        citations: t.citations ?? [],
        meta: t.model ? this.translate.instant('DMS.ASSISTANT.META_SAVED', { model: t.model, seconds: ((t.ms ?? 0) / 1000).toFixed(1) }) : undefined,
        state: 'done'
      }));
      this.turns.set(turns);
      this.conversationId.set(id);
      this.view.set('chat');
      this.scrollDown();
    });
  }

  deleteConversation(id: string, event: Event): void {
    event.stopPropagation();
    this.assistant.deleteConversation(id).subscribe(() => {
      this.history.update((list) => (list ?? []).filter((c) => c.id !== id));
      if (this.conversationId() === id) {
        this.newChat();
        this.view.set('history');
      }
    });
  }

  private scrollDown(): void {
    setTimeout(() => {
      const body = this.body?.nativeElement;
      if (body) {
        body.scrollTop = body.scrollHeight;
      }
    });
  }
}
